import { useEffect, useRef, useState } from 'react';
import { useSpritesStore } from '../../store/spritesSlice';
import { audioService } from '../../services/AudioService';
import './LoadingScreen.scss';

type Phase = 'assets' | 'prompt' | 'audio' | 'done';

/**
 * Boot gate: covers the asset-download / spritesheet-parse lag, then waits for
 * a user gesture (required by the browser autoplay policy) to unlock and
 * preload all audio before the rest of the app mounts.
 */
export const LoadingScreen = ({ onReady }: { onReady: () => void }) => {
  const spritesLoaded = useSpritesStore((s) => s.isLoaded);
  const [phase, setPhase] = useState<Phase>('assets');
  const startedRef = useRef(false);

  useEffect(() => {
    if (spritesLoaded && phase === 'assets') {
      setPhase('prompt');
    }
  }, [spritesLoaded, phase]);

  useEffect(() => {
    if (phase !== 'prompt') {
      return;
    }
    const start = () => {
      if (startedRef.current) {
        return;
      }
      startedRef.current = true;
      setPhase('audio');
      void (async () => {
        try {
          await audioService.unlock();
        } catch {
          // audio failure must not block boot
        }
        setPhase('done');
        onReady();
      })();
    };
    window.addEventListener('keydown', start);
    window.addEventListener('pointerdown', start);
    return () => {
      window.removeEventListener('keydown', start);
      window.removeEventListener('pointerdown', start);
    };
  }, [phase, onReady]);

  if (phase === 'done') {
    return null;
  }

  return (
    <div className="loading-screen">
      {phase === 'prompt' ? (
        <div className="loading-prompt">
          Appuyez sur une touche pour commencer
        </div>
      ) : (
        <>
          <div className="loading-spinner" />
          <div className="loading-text">
            {phase === 'audio' ? "Chargement de l'audio…" : 'Chargement…'}
          </div>
        </>
      )}
    </div>
  );
};
